import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Checkbox, FormControlLabel,
  Button, CircularProgress, Alert, Divider, Chip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import GestureIcon from '@mui/icons-material/Gesture';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://gold-wolf-983825.hostingersite.com/api';

// Divide o HTML do termo em pedaços de HTML estático e campos interativos
// ({{CAMPO: pergunta}} vira caixa de texto, {{SIMNAO: pergunta}} vira Sim/Não)
function dividirConteudo(html) {
  const regex = /\{\{(CAMPO|SIMNAO):\s*([^}]+)\}\}/g;
  const partes = [];
  let lastIndex = 0;
  let match;
  let indice = 0;
  while ((match = regex.exec(html)) !== null) {
    if (match.index > lastIndex) {
      partes.push({ tipo: 'html', conteudo: html.slice(lastIndex, match.index) });
    }
    partes.push({
      tipo: match[1] === 'CAMPO' ? 'campo' : 'simnao',
      label: match[2].trim(),
      key: `resposta_${indice++}`,
    });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < html.length) {
    partes.push({ tipo: 'html', conteudo: html.slice(lastIndex) });
  }
  return partes;
}

// Quadro para assinar com o dedo (ou mouse/caneta). Entrega a imagem PNG da assinatura para o pai.
// Exige um traço de tamanho mínimo, para não aceitar um toque sem querer.
const ALTURA_ASSINATURA = 200;
const TRACO_MINIMO_PX = 80;

function AssinaturaDedo({ onChange }) {
  const canvasRef = useRef(null);
  const desenhando = useRef(false);
  const ultimo = useRef(null);
  const comprimento = useRef(0);
  const [temTraco, setTemTraco] = useState(false);

  const preparar = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = Math.round(w * dpr);
    c.height = Math.round(ALTURA_ASSINATURA * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  useEffect(() => { preparar(); }, [preparar]);

  const posicao = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const exportar = () => {
    const c = canvasRef.current;
    // imagem do tamanho da tela (sem a escala do aparelho), pra ficar leve e previsível
    const saida = document.createElement('canvas');
    saida.width = c.clientWidth;
    saida.height = ALTURA_ASSINATURA;
    saida.getContext('2d').drawImage(c, 0, 0, saida.width, saida.height);
    return saida.toDataURL('image/png');
  };

  const comecar = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    desenhando.current = true;
    ultimo.current = posicao(e);
  };
  const mover = (e) => {
    if (!desenhando.current) return;
    e.preventDefault();
    const p = posicao(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(ultimo.current.x, ultimo.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    comprimento.current += Math.hypot(p.x - ultimo.current.x, p.y - ultimo.current.y);
    ultimo.current = p;
  };
  const terminar = () => {
    if (!desenhando.current) return;
    desenhando.current = false;
    if (comprimento.current >= TRACO_MINIMO_PX) {
      setTemTraco(true);
      onChange(exportar());
    }
  };
  const limpar = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.clientWidth, ALTURA_ASSINATURA);
    comprimento.current = 0;
    setTemTraco(false);
    onChange(null);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Desenhe sua assinatura no quadro abaixo, com o dedo.
      </Typography>
      <Box sx={{ position: 'relative', border: '2px dashed #cbd5e1', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: ALTURA_ASSINATURA, display: 'block', touchAction: 'none', cursor: 'crosshair' }}
          onPointerDown={comecar} onPointerMove={mover} onPointerUp={terminar}
          onPointerCancel={terminar} onPointerLeave={terminar}
        />
        {!temTraco && (
          <Typography sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '1.1rem', pointerEvents: 'none', textAlign: 'center', px: 2 }}>
            Assine aqui
          </Typography>
        )}
        <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 46, borderBottom: '1px solid #e2e8f0', pointerEvents: 'none' }} />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
        <Typography variant="caption" color={temTraco ? 'success.main' : 'text.disabled'}>
          {temTraco ? '✓ Assinatura registrada' : 'Ainda não há assinatura'}
        </Typography>
        <Button size="small" onClick={limpar} sx={{ textTransform: 'none' }}>Apagar e refazer</Button>
      </Box>
    </Box>
  );
}

export default function AssinarDocumento() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [aceito, setAceito] = useState(false);
  const [assinando, setAssinando] = useState(false);
  const [assinado, setAssinado] = useState(false);
  const [respostas, setRespostas] = useState({});
  const [metodo, setMetodo] = useState('dedo'); // 'dedo' | 'autentique' (só anamnese)
  const [assinaturaImg, setAssinaturaImg] = useState(null);
  const [codigoVerificacao, setCodigoVerificacao] = useState('');

  const conteudoComVariaveis = useMemo(() => {
    if (!doc) return '';
    return (doc.conteudo || '')
      .replace(/\{\{PACIENTE_NOME\}\}/g, doc.pacienteNome || '_______________')
      .replace(/\{\{PACIENTE_CPF\}\}/g, doc.pacienteCpf || '_______________')
      .replace(/\{\{DATA\}\}/g, new Date().toLocaleDateString('pt-BR'));
  }, [doc]);

  const partesConteudo = useMemo(() => dividirConteudo(conteudoComVariaveis), [conteudoComVariaveis]);
  const temPerguntas = partesConteudo.some(p => p.tipo === 'campo' || p.tipo === 'simnao');
  // Anamnese pode ser assinada com o dedo ou pela assinatura oficial (Autentique)
  const anamnese = doc?.tipo === 'anamnese' && temPerguntas;
  const assinarComDedo = anamnese && metodo === 'dedo';

  useEffect(() => {
    axios.get(`${API}/open/assinar/${token}`)
      .then(r => {
        setDoc(r.data);
        if (r.data.status === 'assinado') setAssinado(true);
        if (r.data.pacienteNome) setNome(r.data.pacienteNome);
        if (r.data.pacienteCpf) setCpf(r.data.pacienteCpf);
      })
      .catch(() => setErro('Documento não encontrado ou link inválido.'))
      .finally(() => setLoading(false));
  }, [token]);

  const assinar = async () => {
    if (!nome.trim()) return setErro('Informe seu nome completo.');
    if (!aceito) return setErro('Você precisa aceitar os termos para assinar.');
    if (assinarComDedo && !assinaturaImg) return setErro('Desenhe sua assinatura no quadro antes de continuar.');
    setErro('');
    setAssinando(true);
    try {
      const respostasArray = partesConteudo
        .filter(p => p.tipo === 'campo' || p.tipo === 'simnao')
        .map(p => ({ tipo: p.tipo, label: p.label, valor: respostas[p.key] || '' }));
      const resp = await axios.post(`${API}/open/assinar/${token}`, {
        nomeAssinante: nome, cpfAssinante: cpf, aceito,
        ...(respostasArray.length > 0 && { respostas: respostasArray }),
        ...(assinarComDedo && { metodo: 'dedo', assinaturaImagem: assinaturaImg }),
      });
      if (resp.data.codigo) setCodigoVerificacao(resp.data.codigo);
      if (resp.data.autentiqueLink) {
        // Respostas registradas — falta a assinatura oficial, que acontece na Autentique
        window.location.href = resp.data.autentiqueLink;
        return;
      }
      setAssinado(true);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao assinar. Tente novamente.');
    }
    setAssinando(false);
  };

  if (loading) return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f8fafc">
      <CircularProgress sx={{ color: '#F97316' }} />
    </Box>
  );

  if (erro && !doc) return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f8fafc" p={2}>
      <Alert severity="error" sx={{ maxWidth: 480 }}>{erro}</Alert>
    </Box>
  );

  return (
    <Box minHeight="100vh" bgcolor="#f8fafc" py={4} px={2}>
      <Box maxWidth={680} mx="auto">
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GavelIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize="1.1rem">Gerencie Odonto</Typography>
            <Typography variant="caption" color="text.secondary">Assinatura Digital de Documentos</Typography>
          </Box>
        </Box>

        {assinado ? (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 4, textAlign: 'center', border: '1px solid #dcfce7' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="success.main" mb={1}>
              Documento Assinado!
            </Typography>
            <Typography color="text.secondary" mb={2}>
              {doc?.titulo} foi assinado com sucesso.
            </Typography>
            {codigoVerificacao && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, px: 2, py: 1, mb: 2 }}>
                <VerifiedUserIcon sx={{ color: '#16a34a' }} fontSize="small" />
                <Typography variant="body2">Código de verificação: <b>{codigoVerificacao}</b></Typography>
              </Box>
            )}
            {doc?.assinadoEm && (
              <Typography variant="caption" color="text.disabled">
                Assinado em {new Date(doc.assinadoEm).toLocaleString('pt-BR')}
                {doc.nomeAssinante && ` por ${doc.nomeAssinante}`}
              </Typography>
            )}
          </Paper>
        ) : (
          <>
            {/* Documento */}
            <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 2.5, md: 4 }, mb: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Chip label={doc?.tipo === 'anamnese' ? 'Anamnese' : 'Termo de Responsabilidade'}
                  size="small" color={doc?.tipo === 'anamnese' ? 'success' : 'primary'} variant="outlined" />
              </Box>
              <Typography variant="h6" fontWeight={700} mb={3}>{doc?.titulo}</Typography>
              <Divider sx={{ mb: 3 }} />
              {temPerguntas && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Preencha as perguntas abaixo antes de assinar.
                </Alert>
              )}
              {partesConteudo.map((parte, i) => {
                if (parte.tipo === 'html') {
                  return (
                    <Box key={i}
                      sx={{ '& h2, & h3': { fontSize: '1rem', fontWeight: 700, mt: 2, mb: 1 }, '& p': { mb: 1, lineHeight: 1.7 }, '& ul': { pl: 2.5, mb: 1 }, '& li': { mb: 0.5 } }}
                      dangerouslySetInnerHTML={{ __html: parte.conteudo }}
                    />
                  );
                }
                if (parte.tipo === 'campo') {
                  return (
                    <TextField
                      key={parte.key}
                      label={parte.label}
                      fullWidth multiline minRows={1} size="small"
                      value={respostas[parte.key] || ''}
                      onChange={e => setRespostas(r => ({ ...r, [parte.key]: e.target.value }))}
                      sx={{ my: 1.5 }}
                    />
                  );
                }
                // simnao
                return (
                  <Box key={parte.key} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    my: 1.5, py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)',
                  }}>
                    <Typography sx={{ fontSize: '0.92rem' }}>{parte.label}</Typography>
                    <ToggleButtonGroup
                      exclusive size="small"
                      value={respostas[parte.key] || null}
                      onChange={(e, v) => v && setRespostas(r => ({ ...r, [parte.key]: v }))}
                    >
                      <ToggleButton value="Sim" sx={{ px: 2 }}>Sim</ToggleButton>
                      <ToggleButton value="Não" sx={{ px: 2 }}>Não</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                );
              })}
            </Paper>

            {/* Assinatura */}
            <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 2.5, md: 4 }, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography fontWeight={700} mb={2}>Assinar Documento</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Nome completo *"
                  fullWidth value={nome}
                  onChange={e => setNome(e.target.value)}
                  placeholder="Digite seu nome completo como assinatura"
                />
                <TextField
                  label="CPF (opcional)"
                  fullWidth value={cpf}
                  onChange={e => setCpf(e.target.value)}
                  placeholder="000.000.000-00"
                />
                {anamnese && (
                  <Box>
                    <Typography variant="body2" fontWeight={600} mb={1}>Como você prefere assinar?</Typography>
                    <ToggleButtonGroup
                      exclusive fullWidth size="small" value={metodo}
                      onChange={(e, v) => v && setMetodo(v)}
                      sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 1.2, fontWeight: 600 },
                            '& .Mui-selected': { bgcolor: 'rgba(249,115,22,0.12) !important', color: '#c2410c !important' } }}
                    >
                      <ToggleButton value="dedo"><GestureIcon fontSize="small" sx={{ mr: 0.8 }} /> Com o dedo na tela</ToggleButton>
                      <ToggleButton value="autentique">Assinatura oficial (Autentique)</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
                {assinarComDedo && <AssinaturaDedo onChange={setAssinaturaImg} />}
                <FormControlLabel
                  control={<Checkbox checked={aceito} onChange={e => setAceito(e.target.checked)} sx={{ color: '#F97316', '&.Mui-checked': { color: '#F97316' } }} />}
                  label={<Typography variant="body2">Li e concordo com o conteúdo do documento acima e assino digitalmente.</Typography>}
                />
                {erro && <Alert severity="error">{erro}</Alert>}
                <Button
                  variant="contained" size="large" fullWidth
                  onClick={assinar} disabled={assinando || !aceito}
                  startIcon={assinando ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                  sx={{ bgcolor: '#F97316', '&:hover': { bgcolor: '#ea580c' }, borderRadius: 2, py: 1.5, fontWeight: 700 }}
                >
                  {assinando ? 'Enviando...' : assinarComDedo ? 'Assinar documento' : temPerguntas ? 'Continuar para Assinatura' : 'Assinar Documento'}
                </Button>
                <Typography variant="caption" color="text.disabled" textAlign="center">
                  {assinarComDedo
                    ? 'Ao assinar, sua assinatura, nome, data, horário, aparelho e IP ficam registrados como comprovante.'
                    : temPerguntas
                      ? 'Você será direcionado para a assinatura digital oficial (Autentique) na próxima etapa.'
                      : 'Ao assinar, seu IP e horário são registrados como comprovante.'}
                </Typography>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}
